import { format } from 'date-fns';
import type {
  PurchaseInvoiceDetailRow,
  PurchaseInvoiceExpenseDetailRow,
  PurchaseInvoiceExpenseRow,
  PurchaseInvoiceFormValues,
  PurchaseInvoiceLookup,
  PurchaseInvoicePayload,
  PurchaseInvoiceTaxRow,
  PurchaseInvoiceTotals
} from '../types';
import { transformDocumentNumber } from '@/components/custom/erp';
import type { FormFieldState } from '../hooks/useFormFieldState';
import { toFiniteNumber, roundNumber } from '@/lib/formatUtils';
import { MA_CHUNG_TU } from '@/constants/ma-chung-tu';
import { isValidUUID } from '@/lib/uuid-validator';
import type { FormMode } from '@/types/form';

const lookupUUID = (value?: PurchaseInvoiceLookup | string | null) => (typeof value === 'string' ? value : value?.uuid);

/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */

const transformDetailRows = (
  detailRows: PurchaseInvoiceDetailRow[],
  mode: FormMode,
  initialDetailRows?: PurchaseInvoiceDetailRow[]
): PurchaseInvoiceDetailRow[] => {
  return detailRows.map((row: PurchaseInvoiceDetailRow, index: number) => {
    const initialRow = initialDetailRows?.find((ct: PurchaseInvoiceDetailRow) => ct.uuid === row.uuid);
    return {
      line: index + 1,
      ...(isValidUUID(row.uuid) && mode !== 'add' ? { uuid: row.uuid } : {}),
      sl_cl: roundNumber(
        toFiniteNumber(row.so_luong) - toFiniteNumber(initialRow?.so_luong) + toFiniteNumber(initialRow?.sl_cl)
      ),

      ma_vt: row.ma_vt_data?.uuid || '',
      dvt: row.dvt || row.ma_vt_data?.dvt || '',

      ma_kho: row.ma_kho_data?.uuid || '',
      so_luong: row.so_luong || 0,
      gia_nt0: roundNumber(row.gia_nt0) || 0,
      tien_nt0: roundNumber(row.tien_nt0) || roundNumber(toFiniteNumber(row.so_luong) * toFiniteNumber(row.gia_nt0)),
      cp_nt: row.cp_nt || 0,
      gia_nt: roundNumber(row.ma_vt_data?.gia_ton) || 0,
      gia_ton: roundNumber(row.gia_nt) || 0,
      gia_ton_nt: roundNumber(row.gia_nt) || 0,

      ma_thue: row.ma_thue_data?.uuid || '',
      thue_nt: roundNumber(row.thue_nt) || 0,

      tl_ck: row.tl_ck || 0,

      tien_tck: roundNumber(row.tien_tck_nt) || 0,
      tien_tck_nt: roundNumber(row.tien_tck_nt) || 0,

      ck_nt: row.ck_nt || 0,
      ck: row.ck_nt || 0,

      tk_cpxt: row.tk_cpxt_data?.uuid || '',
      tk_vt: row.ma_vt_data?.tk_khcp_data?.uuid || row.tk_vt_data?.uuid || '',

      px_dd: row.px_dd || false,

      ma_bp: row.ma_bp_data?.uuid || '',
      ma_vv: row.ma_vv_data?.uuid || null,
      ma_hd: row.ma_hd_data?.uuid || null,
      ma_dtt: row.ma_dtt_data?.uuid || null,
      ma_ku: row.ma_ku_data?.uuid || null,
      ma_phi: row.ma_phi_data?.uuid || null,
      ma_cp0: row.ma_cp0_data?.uuid || null,

      id_dh: lookupUUID(row.id_dh),
      line_dh: lookupUUID(row.line_dh),
      so_ct_dh: row.so_ct_dh || '',
      so_ct_hd4: row.so_ct_hd4 || '',
      line_dh4: row.line_dh4 || ''
    };
  });
};

const transformChiPhiRows = (chiPhiRows: PurchaseInvoiceExpenseRow[], mode: FormMode): PurchaseInvoiceExpenseRow[] => {
  return chiPhiRows?.map((row: PurchaseInvoiceExpenseRow, index: number) => ({
    line: index + 1,
    ...(isValidUUID(row.uuid) && mode !== 'add' ? { uuid: row.uuid } : {}),
    ma_cp: row.ma_cp_data?.uuid || null,
    ten_cp: row.ma_cp_data?.ten_cp || row.ten_cp || '',

    tien_cp_nt: row.tien_cp_nt || 0,
    tien_cp: row.tien_cp || 0,

    ma_kh: row.ma_kh_data?.uuid || null,
    ten_kh: row.ma_kh_data?.customer_name || row.ten_kh || '',

    tk: row.tk_data?.uuid || ''
  }));
};

const transformChiPhiChiTietRows = (
  chiPhiChiTietRows: PurchaseInvoiceExpenseDetailRow[],
  mode: FormMode
): PurchaseInvoiceExpenseDetailRow[] => {
  return chiPhiChiTietRows?.map((row: PurchaseInvoiceExpenseDetailRow, index: number) => ({
    line: index + 1,
    ...(isValidUUID(row.uuid) && mode !== 'add' ? { uuid: row.uuid } : {}),
    ma_cp: row.ma_cp_data?.uuid || null,
    ma_vt: row.ma_vt_data?.uuid || null,

    tien_cp_nt: row.tien_cp_nt || 0,
    tien_cp: row.tien_cp_nt || 0,

    line_vt: row.line_vt || 1,
    line_cp: row.line_cp || 1
  }));
};

const transformThueRows = (
  thueRows: PurchaseInvoiceTaxRow[],
  mode: FormMode,
  state: FormFieldState,
  dien_giai: string,
  so_ct0: string,
  so_ct2: string
): PurchaseInvoiceTaxRow[] => {
  return thueRows?.map((row: PurchaseInvoiceTaxRow, index: number) => ({
    line: index + 1,
    ...(isValidUUID(row.uuid) && mode !== 'add' ? { uuid: row.uuid } : {}),
    so_ct0: row.so_ct0 || so_ct0,
    so_ct2: row.so_ct2 || so_ct2,
    ngay_ct0: row.ngay_ct0 ? format(row.ngay_ct0, 'yyyy-MM-dd') : '',

    ma_thue: row.ma_thue_data?.uuid || '',
    thue_suat: row.thue_suat || 0,
    ma_mau_ct: row.ma_mau_ct_data?.uuid || null,
    ma_mau_bc: row.ma_mau_bc || '3',
    ma_tc_thue: row.ma_tc_thue_data?.uuid || '',

    ma_kh: row.ma_kh_data?.uuid || state.khachHang?.uuid || '',
    ten_kh_thue: row.ma_kh_data?.customer_name || row.ten_kh_thue || state.khachHang?.customer_name || '',
    dia_chi: row.dia_chi || state.khachHang?.address || row.ma_kh_data?.address || '',
    ma_so_thue: row.ma_so_thue || state.khachHang?.tax_code || row.ma_kh_data?.tax_code || '',

    ten_vt_thue: row.ten_vt_thue || dien_giai || row.ma_thue_data?.ten_thue,

    t_tien_nt: roundNumber(row.t_tien_nt) || 0,
    t_tien: roundNumber(row.t_tien) || 0,

    tk_thue_no: row.tk_thue_no_data?.uuid || '',
    ten_tk_thue_no: row.tk_thue_no_data?.customer_name || '',

    t_thue_nt: roundNumber(row.t_thue_nt) || 0,
    t_thue: roundNumber(row.t_thue) || 0,
    ma_kh9: row.ma_kh9_data?.uuid || null,
    ma_tt: row.ma_tt_data?.uuid || null,
    ghi_chu: row.ghi_chu || '',
    ma_bp: row.ma_bp_data?.uuid || null,
    ma_vv: row.ma_vv_data?.uuid || null,
    ma_hd: row.ma_hd_data?.uuid || null,
    ma_dtt: row.ma_dtt_data?.uuid || null,
    ma_ku: row.ma_ku_data?.uuid || null,
    ma_phi: row.ma_phi_data?.uuid || null,
    ma_cp0: row.ma_cp0_data?.uuid || null,

    so_ct_dh: row.so_ct_dh || ''
  }));
};

/**
 * Transform all form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @param accountRows - Array of account row data
 * @returns Transformed data ready for API submission
 */
export const transformFormData = (
  data: PurchaseInvoiceFormValues,
  state: FormFieldState,
  mode: FormMode,
  entityUnit: { uuid?: string } | null | undefined,
  detailRows: PurchaseInvoiceDetailRow[],
  chiPhiRows: PurchaseInvoiceExpenseRow[],
  chiPhiChiTietRows: PurchaseInvoiceExpenseDetailRow[],
  thueRows: PurchaseInvoiceTaxRow[],
  totals: PurchaseInvoiceTotals,
  initialDetailRows?: PurchaseInvoiceDetailRow[]
): PurchaseInvoicePayload => {
  const detail = transformDetailRows(detailRows, mode, initialDetailRows);
  const chiPhi = transformChiPhiRows(chiPhiRows, mode);
  const chiPhiChiTiet = transformChiPhiChiTietRows(chiPhiChiTietRows, mode);
  const thue = transformThueRows(thueRows, mode, state, data.dien_giai || '', data.so_ct0 || '', data.so_ct2 || '');

  return {
    ...data,
    unit_id: entityUnit?.uuid || '',

    hdmh_yn: state.hdmh_yn || false,
    pn_yn: state.pn_yn || false,
    pc_tao_yn: state.pc_tao_yn || false,
    xt_yn: state.xt_yn || false,
    ck_yn: state.ck_yn || false,
    loai_ck: state.ck_yn ? state.loai_ck : '4',
    ck_tl_nt: state.ck_tl_nt || 0,

    ma_httt: state.ma_httt || 'TMM',

    ma_kh: state.khachHang?.uuid || '',
    ma_so_thue: data.ma_so_thue || state.khachHang?.tax_code || '',
    ten_kh: data.ten_kh || state.khachHang?.customer_name || '',
    dia_chi: data.dia_chi || state.khachHang?.address || '',
    ong_ba: data.ong_ba || state.khachHang?.contact_person || '',
    e_mail: data.e_mail || state.khachHang?.email || null,
    dien_giai: data.dien_giai || '',

    ma_nvmh: state.nhanVien?.uuid || state.khachHang?.sales_rep_data?.uuid || '',
    tk: state.taiKhoan?.uuid || state.khachHang?.account_data?.uuid || '',
    ma_tt: state.hanThanhToan?.uuid || state.khachHang?.payment_term_data?.uuid || '',

    ma_ngv: '1',
    i_so_pn: transformDocumentNumber(state.quyenPhieuNhap, state.soPhieuNhap || '', MA_CHUNG_TU.TON_KHO.PHIEU_NHAP_KHO)
      .i_so_ct,
    ma_nk_pn: transformDocumentNumber(state.quyenPhieuNhap, state.soPhieuNhap || '', MA_CHUNG_TU.TON_KHO.PHIEU_NHAP_KHO)
      .ma_nk,
    so_pn: transformDocumentNumber(state.quyenPhieuNhap, state.soPhieuNhap || '', MA_CHUNG_TU.TON_KHO.PHIEU_NHAP_KHO)
      .so_ct,
    ...transformDocumentNumber(state.quyenChungTu, state.soChungTu || ''),
    so_ct2: data.so_ct2 || '',
    ngay_ct: data.ngay_ct || '',
    ngay_lct: data.ngay_ct || new Date().toISOString().split('T')[0],

    so_ct_hddt0: data.so_ct_hddt0 || '',
    ngay_ct_hddt0: data.ngay_ct_hddt0 || '',
    so_ct2_hddt0: data.so_ct2_hddt0 || '',

    t_so_luong: roundNumber(totals.so_luong) || 0,
    t_tien_nt: roundNumber(totals.tong_tien) || 0,
    t_tien: roundNumber(totals.tong_tien) || 0,
    t_cp_nt: roundNumber(data.t_cp_nt) || 0,
    t_cp: roundNumber(data.t_cp || data.t_cp_nt) || 0,
    t_thue_nt: roundNumber(totals.thue) || 0,
    t_thue: roundNumber(totals.thue) || 0,
    t_ck_nt: roundNumber(totals.chiet_khau) || 0,
    t_ck: roundNumber(totals.chiet_khau) || 0,
    t_tt_nt: roundNumber(totals.tong_thanh_toan) || 0,
    t_tt: roundNumber(totals.tong_thanh_toan) || 0,

    chi_tiet_hoa_don: detail,
    chi_phi_hoa_don: chiPhi,
    chi_phi_chi_tiet_hoa_don: chiPhiChiTiet,
    thue_hoa_don: thue,

    phieu_chi_uuids: data.phieu_chi_uuids || [],
    phieu_nhap_kho_uuid: data.phieu_nhap_kho_uuid || '',
    giay_bao_no_uuids: data.giay_bao_no_uuids || []
  };
};
